<template>
  <div>
    <v-container>
      <div class="back-link" @click="router.push({ name: 'AccountView' })">
        <img src="@/assets/images/icons/arrow-left.svg" alt="Back" class="back-icon" />
        <span>Return to Account</span>
      </div>

      <v-card>
        <v-card-title>Add Structure Capture Enterprise</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Add Capture Enterprise to your existing subscription. You'll be charged the
            Capture Enterprise monthly fee in addition to your current SDK subscription.
          </p>

          <div v-if="!stripeLoaded && !errorMessage" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-4 text-medium-emphasis">Loading Secure Payment Form...</p>
          </div>

          <div id="add-capture-checkout"></div>

          <v-alert v-if="errorMessage" type="error" class="mt-4">
            {{ errorMessage }}
            <v-btn class="mt-2 ml-2" size="small" color="white" variant="text" @click="initializeCheckout">
              Retry
            </v-btn>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

const router = useRouter();
const store = useStore();
const { getAccessTokenSilently } = useAuth0();

const customerType = computed(() => store.state.authentication.customer_type);
const stripeLoaded = ref(false);
const errorMessage = ref(null);
const checkoutRef = ref(null);
const isInitializing = ref(false);

const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
const BASE_URL = process.env.VUE_APP_CBE_BASE_URL;

async function initializeCheckout() {
  if (customerType.value === 'capture' || customerType.value === 'both') {
    errorMessage.value = 'You already have Capture Enterprise. Please contact support to manage your subscription.';
    return;
  }

  if (isInitializing.value) return;
  isInitializing.value = true;
  errorMessage.value = null;

  if (checkoutRef.value) {
    checkoutRef.value.destroy();
    checkoutRef.value = null;
    stripeLoaded.value = false;
  }

  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    const tiersResponse = await axios.get(`${BASE_URL}/piccolo/admin/tiers`, { headers });
    const captureTier = tiersResponse.data.find(t => t.name === 'Structure Capture Enterprise');
    if (!captureTier?.stripe_price_id) {
      errorMessage.value = 'Capture Enterprise pricing not found. Please contact support.';
      return;
    }

    const stripe = await loadStripe(publishableKey);
    if (!stripe) {
      errorMessage.value = 'Failed to load the payment form. Please check your internet connection or disable ad-blockers and try again.';
      return;
    }

    const { data } = await axios.post(
      `${BASE_URL}/stripe/create-subscription`,
      { capture_price_id: captureTier.stripe_price_id },
      { headers }
    );

    if (!data.clientSecret) {
      errorMessage.value = 'Failed to initialize checkout. Please try again.';
      return;
    }

    const checkout = await stripe.initEmbeddedCheckout({ clientSecret: data.clientSecret });
    checkoutRef.value = checkout;
    checkout.mount('#add-capture-checkout');
    stripeLoaded.value = true;
  } catch (err) {
    errorMessage.value =
      err.response?.data?.error ||
      err.response?.data?.detail ||
      err.message ||
      'Failed to load payment form. Please try again.';
  } finally {
    isInitializing.value = false;
  }
}

onMounted(initializeCheckout);

onUnmounted(() => {
  if (checkoutRef.value) {
    checkoutRef.value.destroy();
    checkoutRef.value = null;
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/AddCaptureProduct.scss'
</style>
